import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Flex, Heading, IconButton, Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetNpsScoreQuery } from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import { useMeetingStatsCreationContext } from '@/features/meeting-stats/context/MeetingStatsCreationContext';
import { AppSpinner } from '@/ui/AppSpinner';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { NpsRatingBox } from './NpsRatingBox';

const RateMeetingUserView = () => {
  const [nbrUsers, setNbrUsers] = useState<number>(1);
  const [currNbrUsers, setCurrNbrUsers] = useState<number>(1);
  const { t } = useTranslation();
  const { currentMeeting } = useCurrentMeetingDataContext();
  const { userID } = useLoggedInUserAuthContext();
  const { createNPSScoreResult } = useMeetingStatsCreationContext();
  const [votes, setVotes] = useState<number[]>([]);
  const userNPSVoteQuery = useGetNpsScoreQuery({
    variables: {
      where: {
        userId: { equals: userID },
        meetingId: { equals: currentMeeting.id },
      },
    },
    skip: !currentMeeting || !userID,
  });
  const hasUserVoted = useMemo(() => {
    if (userNPSVoteQuery.loading) {
      return true;
    }
    return !!userNPSVoteQuery.data?.getNPSScore;
  }, [userNPSVoteQuery.loading, userNPSVoteQuery.data]);

  const handleVote = (userIndex: number, rating: number) => {
    setVotes((prev) => {
      const newVotes = [...prev];
      newVotes.push(rating);
      return newVotes;
    });
  };

  useEffect(() => {
    if (votes.length === nbrUsers && nbrUsers !== 0 && !hasUserVoted && !userNPSVoteQuery.loading) {
      // NOTE: Here we add a local storage check to make sure we do not send a result twice
      const lastVotedMeetingId = localStorage.getItem('lastVotedMeetingId');
      const lastVotedMeetingStartTime = localStorage.getItem('lastVotedMeetingStartTime');

      if (
        lastVotedMeetingId &&
        lastVotedMeetingId === currentMeeting.id &&
        lastVotedMeetingStartTime &&
        lastVotedMeetingStartTime === currentMeeting.startDate?.toString()
      ) {
        captureSentry({
          title:
            'NPSScore: Tried to vote a second time but local cookie check prevented it from happenning.',
          detail: {
            lastVotedMeetingId,
            currentMeetingId: currentMeeting.id,
            votes,
            nbrUsers,
            hasUserVotedQueryData: hasUserVoted,
            hasUserVotedQueryLoading: userNPSVoteQuery.loading,
          },
        });
        return;
      }
      void createNPSScoreResult(currentMeeting.id, votes).then(() => {
        void userNPSVoteQuery.refetch();
      });
      captureSentry({
        title: 'NPSScore Vote (not an error)',
        detail: {
          meetingID: currentMeeting.id,
          userID: userID,
          votes,
          nbrUsers,
          hasUserVotedQueryData: hasUserVoted,
        },
      });
    }
  }, [votes.length, currentMeeting?.id, userID, hasUserVoted]);

  if (userNPSVoteQuery.loading) {
    return <AppSpinner />;
  }

  return (
    <Flex
      flex="auto"
      height="100%"
      direction="column"
      justifyContent="center"
      boxShadow="0px 0px 2px #eee"
      borderRadius="5px"
      padding="10px"
      textAlign="center"
    >
      <VStack background="white" justifyContent="center" alignItems="center" userSelect="none">
        <Heading color="livelinx.purple200" fontWeight="200em" fontSize="2em">
          {t('npsScore.title.user')}
        </Heading>
        <Text fontSize="1.5em" color="livelinx.grey200">
          {!hasUserVoted &&
            (nbrUsers === 0 ? t('npsScore.nbrUsers.question') : t('npsScore.description.user'))}
          {hasUserVoted && t('npsScore.thanks.title')}
        </Text>
      </VStack>

      <Flex
        background="white"
        padding="20px"
        justifyContent="center"
        alignItems="center"
        gap="20px"
      >
        {!hasUserVoted && nbrUsers === 0 && (
          <Flex direction="column">
            <IconButton
              size="md"
              borderRadius="0"
              border="none"
              background="livelinx.purple200"
              color="#FFF"
              aria-label="up-amt-users"
              icon={<UpOutlined />}
              onClick={() => {
                if (currNbrUsers >= 3) return;
                setCurrNbrUsers((prev) => prev + 1);
              }}
              sx={{ marginBottom: '10px' }}
            />
            <Text fontSize="1.5em" color="livelinx.grey200">
              {currNbrUsers}
            </Text>
            <IconButton
              size="md"
              borderRadius="0"
              border="none"
              background="livelinx.purple200"
              color="#FFF"
              aria-label="up-amt-users"
              icon={<DownOutlined />}
              onClick={() => {
                if (currNbrUsers <= 1) return;
                setCurrNbrUsers((prev) => prev - 1);
              }}
              sx={{ marginTop: '10px' }}
            />
            <Button sx={{ marginTop: '10px' }} onClick={() => setNbrUsers(currNbrUsers)}>
              Confirm
            </Button>
          </Flex>
        )}
        {!hasUserVoted &&
          nbrUsers > 0 &&
          Array.from({ length: nbrUsers }).map((_, i) => (
            <NpsRatingBox
              key={i}
              userIndex={i + 1}
              onSubmit={(rating) => {
                handleVote(i, rating);
              }}
            />
          ))}
      </Flex>
    </Flex>
  );
};

export default RateMeetingUserView;
